import { PaymentCategoryOption } from 'constants/beneficiary.constants';

/**
 * Formats a payment category option into a human-readable display string
 * with hierarchy separated by slashes (e.g., "Primary / Secondary / Tertiary")
 *
 * @param option - Payment category option object containing primary, secondary, tertiary fields
 * @param fallbackId - Fallback ID to use if option is not provided
 * @param t - Optional translation function for internationalization
 * @returns Formatted string with translated category names separated by " / "
 *
 * @example
 * formatPaymentCategory(option, '123', t) // Returns: "Goods / Marketing Expenses / International"
 */
export const formatPaymentCategory = (
	option?: PaymentCategoryOption,
	fallbackId?: string,
	t?: (key: string, fallback?: string) => string
): string => {
	if (!option && !fallbackId) return '';

	const primary = option?.primary || option?.label || fallbackId || '';
	const secondary = option?.secondary?.trim();
	const tertiary = option?.tertiary
		?.split(',')
		.map((t) => t.trim())
		.filter(Boolean)?.[0];

	const parts: string[] = [];

	if (primary) {
		parts.push(t ? t(`payment_category.name.${primary}`, primary) : primary);
	}
	if (secondary && secondary !== primary) {
		parts.push(t ? t(`payment_category.name.${secondary}`, secondary) : secondary);
	}
	if (tertiary && tertiary !== primary && tertiary !== secondary) {
		parts.push(t ? t(`payment_category.name.${tertiary}`, tertiary) : tertiary);
	}

	return parts.join(' / ');
};

/**
 * Creates a map of payment category options indexed by their ID
 * @param options - Array of payment category options
 * @returns Record mapping category IDs to their full option objects
 */
export const createCategoryMap = (options: PaymentCategoryOption[]): Record<string, PaymentCategoryOption> => {
	return options.reduce((acc, option) => {
		acc[option.id] = option;
		return acc;
	}, {} as Record<string, PaymentCategoryOption>);
};

/**
 * Normalizes payment categories to ensure they are always PaymentCategoryOption objects
 * Handles cases where categories might be stored as IDs (strings/numbers) or as full objects
 * @param categories - Array of categories that could be IDs or objects
 * @returns Array of PaymentCategoryOption objects, or empty array if invalid
 */
export const normalizePaymentCategories = (
	categories?: string[] | number[] | PaymentCategoryOption[]
): PaymentCategoryOption[] => {
	if (!categories || !Array.isArray(categories) || categories.length === 0) {
		return [];
	}

	const firstItem = categories[0];

	// Check if it's an array of IDs (strings or numbers)
	if (typeof firstItem === 'string' || typeof firstItem === 'number') {
		// IDs only - can't convert to full objects without fetching data
		// Return empty array as we need full objects for proper display
		return [];
	}

	// It's already an array of PaymentCategoryOption objects
	return categories as PaymentCategoryOption[];
};

/**
 * Formats a category tree from backend into "Primary / Secondary / Tertiary" display string
 * @param categoryTree - Hierarchical tree structure from backend
 * @param t - Translation function
 * @returns Formatted string like "טובין / הוצאות פרסום בחו״ל"
 */
export const formatCategoryTreeForDisplay = (
	categoryTree: any[],
	t: (key: string, fallback?: string) => string
): string => {
	if (!categoryTree || !Array.isArray(categoryTree) || categoryTree.length === 0) {
		return '';
	}

	const parts: string[] = [];

	// Traverse the tree to collect primary -> secondary -> tertiary
	const traverse = (nodes: any[], depth: number = 0) => {
		if (!nodes || !Array.isArray(nodes) || nodes.length === 0 || depth > 2) {
			return;
		}

		// Take the first node at each level (since we filter by single category ID)
		const node = nodes[0];
		if (!node) {
			return;
		}

		// Get the category name - make sure we're using the 'name' property, not 'id'
		const categoryName = node.name || node.categoryName || String(node.id);
		if (!categoryName || typeof categoryName !== 'string') {
			return;
		}

		const translationKey = `payment_category.name.${categoryName}`;
		// Use a clear fallback to debug
		const translated = t(translationKey, categoryName);
		// Add the translation (or fallback if translation doesn't exist)
		if (translated && typeof translated === 'string' && translated.trim().length > 0) {
			parts.push(translated.trim());
		}

		// Recurse into children
		if (node.children && Array.isArray(node.children) && node.children.length > 0) {
			traverse(node.children, depth + 1);
		}
	};

	traverse(categoryTree);
	const result = parts.filter((p) => p && p.length > 0).join(' / ');
	return result;
};
